import { useContext } from "react";

//Common
import {
  ConfirmationDialog,
  DataGrid,
  Drawer,
  Snackbar,
} from "../common/VFX-UI";
import PageHeader from "../common/PageHeader/PageHeader";
import MoreActionsMenu from "../common/MoreActionsMenu/MoreActionsMenu";
import { MoreActionButton } from "../common/Buttons";
import SearchContainer from "../common/Forms/SearchContainer/SearchContainer";

//App
import { AgencyProps } from "../../types/agency";
import AgencyView from "./AgencyView";
import useAgency from "./hooks/useAgency";

import { SearchProps } from "../../types";
import { SearchDefaultValue } from "../../store/types";
import { SearchFieldsDefaultValue } from "../../utility/constants/constants";

import { ColumnDescription } from "react-bootstrap-table-next";
import { AgencyContext } from "./AgencyContainer";
import { AgencyDtoDto } from "../../app/model/api/types/dto/agencyDto";
import useComponentGeneralStyles from "../styles";
import { AgencyFormContainer } from "./AgencyFormContainer";


const Agency: React.FC<AgencyProps> = (props) => {
  const {
    data: agencies,
    activateMutation,
    deleteMutation,
    fullscreen,
  } = props;
  const generalStyles = useComponentGeneralStyles();

  const agencyContext = useContext(AgencyContext);

  const hasManageAgenciesPermission =
    agencyContext?.hasManageAgenciesPermission;

  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleDeactivateHandler,
    toggleActivateHandler,
    handleDeactivate,
    handleActivate,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
    handleActiveToggleChange,
  } = useAgency({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
  });

  const openMenu = Boolean(actionColumnAnchorEl);

  const searchProps: SearchProps<AgencyDtoDto> = {
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };


  var columns: ColumnDescription[] = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
  ];

  if (fullscreen === null || fullscreen === undefined || fullscreen) {
    columns = [
      ...columns,
      {
        dataField: "name",
        text: "Name",
        sort: true,
      },
    ];
  } else {
    columns = [
      ...columns,
      {
        dataField: "name",
        text: "Name",
        sort: true,
      },
    ];
  }

  columns = [
    ...columns,
    {
      dataField: "action",
      text: "",
      style: { width: "25px" },
      isDummyField: true,
      formatter: () => {
        return (
          <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
        );
      },
    },
  ];

  return (
    <>
      <PageHeader>Agencies</PageHeader>
      <div style={generalStyles.searchContainer}>
        <SearchContainer
          searchProps={searchProps}
          activeToggle={formContext?.activeToggle ?? true}
          handleActiveToggleChange={handleActiveToggleChange}
          toggleNewViewHandler={
            hasManageAgenciesPermission ? toggleNewViewHandler : undefined
          }
        />
      </div>

      <Drawer show={state.toggleNewForm}>
        {state.toggleNewForm && (
          <AgencyFormContainer
            onClose={toggleNewViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleEditForm}>
        {state.toggleEditForm && formContext?.data && (
          <AgencyFormContainer
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleView}>
        {state.toggleView && formContext?.data && (
          <AgencyView
            onClose={toggleViewHandler}
            editAction={() => {
              toggleEditViewHandler();
            }}
            toggleDeactivateHandler={toggleDeactivateHandler}
            toggleActivateHandler={toggleActivateHandler}
          />
        )}
      </Drawer>

      <DataGrid
        columns={columns}
        data={agencies}
        keyField="id"
        rowEvents={rowEvents}
        totalSize={formContext?.paging?.totalSize}
        page={formContext?.paging?.currentPage}
        pageSize={formContext?.pageSize}
        remote={true}
        handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
          if (formContext?.setCurrentPage && newPage) {
            await formContext?.setCurrentPage(newPage);
          }
          if (formContext?.setCurrentPageSize && pageSize) {
            await formContext?.setCurrentPageSize(pageSize);
          }
          if (formContext?.setSortBy && sortField && sortOrder) {
            await formContext?.setSortBy({
              field: sortField,
              direction: sortOrder === "asc" ? 1 : -1,
            });
          }
          formContext?.refetchGrid();
        }}
      />

      {actionColumnAnchorEl && (
        <MoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            toggleViewHandler();
            setActionColumnAnchorEl(null);
          }}
        />
      )}

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivate}
        contentTitle={`Deactivate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivate}
        contentTitle={`Activate ${formContext?.data?.name}`}
        contentText={"Are you sure you want to continue?"}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />

      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}
    </>
  );
};

export default Agency;
